import { TextAlignStartIcon, TimerIcon, UserLockIcon } from "lucide-react";

import { EnumColors } from "@/constants/values";
import { iconsByTypename } from "@/objects/@icons";
import { ObjectType } from "@/objects/@types";

type RuleDelegationBase = {
  id: string;
  identifier: string;
  role?: string | null;
  canAutoApply?: boolean | null;
  expiresAt?: string | null;
  entity?: {
    id: string;
    identifier: string;
    name?: string | null;
    __typename?: "EntityObject";
  } | null;
  __typename?: "RuleDelegationObject";
};

const object: ObjectType<RuleDelegationBase> = {
  humanName: "rule delegation",
  inputKey: "ruleDelegation",
  typename: "RuleDelegationObject",
  ReactIcon: UserLockIcon,
  isEntity: false,
  getTitle: (data) =>
    data?.entity?.name ?? data?.entity?.identifier ?? data?.identifier,
  getEntityId: (data) => data?.id,
  fieldDefinitions: [
    {
      objectKey: "entity",
      label: "Entity",
      type: "reference",
      ReactIcon: iconsByTypename.ContactObject,
      asString: (data) => data?.entity?.name ?? data?.entity?.identifier ?? "",
      getValue: (data) =>
        data?.entity
          ? {
              __typename: "EntityObject" as const,
              id: data.entity.id,
              identifier: data.entity.identifier,
              name: data.entity.name ?? undefined,
            }
          : null,
      updateProps: { isRequired: true, isEditable: false },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "reference",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "role",
      label: "Role",
      type: "enum",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => data?.role ?? "",
      getValue: (data) => data?.role ?? "",
      getValueOptions: () => ({
        enum: [
          { key: "OWNER", value: "OWNER" },
          { key: "EDITOR", value: "EDITOR" },
          { key: "OPERATOR", value: "OPERATOR" },
          { key: "VIEWER", value: "VIEWER" },
        ],
        getColors: (mode) => ({
          OWNER: EnumColors.PURPLE[mode],
          EDITOR: EnumColors.BLUE[mode],
          OPERATOR: EnumColors.GREEN[mode],
          VIEWER: EnumColors.GREY_1[mode],
        }),
      }),
      updateProps: { isRequired: true, isEditable: true },
      summaryProps: { isVisible: true },
      fieldProps: {
        headerFieldType: "enum",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "canAutoApply",
      label: "Auto apply",
      type: "boolean",
      ReactIcon: TextAlignStartIcon,
      asString: (data) => (data?.canAutoApply ? "Yes" : "No"),
      getValue: (data) => Boolean(data?.canAutoApply),
      updateProps: { isEditable: true },
      fieldProps: {
        headerFieldType: "boolean",
        isSortable: true,
        isFilterable: true,
      },
    },
    {
      objectKey: "expiresAt",
      label: "Expires",
      type: "date",
      ReactIcon: TimerIcon,
      asString: (data) => data?.expiresAt ?? "",
      getValue: (data) => data?.expiresAt ?? "",
      updateProps: { isEditable: true },
      fieldProps: {
        headerFieldType: "date",
        isSortable: true,
        isFilterable: true,
      },
    },
  ],
};

export { object as RuleDelegationObject };
